/**
 * 翻译资源缓存管理器
 * 优化翻译资源的加载方式，避免阻塞页面功能
 * 实现翻译内容的内存缓存
 */

class ResourceCache {
  constructor() {
    // 翻译资源缓存
    this.translationCache = new Map()
    
    // 缓存统计信息
    this.stats = {
      hits: 0,
      misses: 0,
      loadTime: 0,
      cacheSize: 0
    }
    
    // 缓存配置
    this.config = {
      maxCacheSize: 1000, // 最大缓存条目数
      maxMemoryUsage: 5 * 1024 * 1024, // 最大内存使用量 5MB
      ttl: 30 * 60 * 1000, // 缓存生存时间 30分钟
      preloadKeys: [
        'common.loading',
        'common.error',
        'common.success',
        'titles.home',
        'titles.overview'
      ]
    }
    
    // 预加载状态
    this.preloadStatus = {
      isPreloading: false,
      preloadedKeys: new Set(),
      preloadPromise: null
    }
  }

  /**
   * 获取缓存键
   * @param {string} locale - 语言代码
   * @param {string} key - 翻译键
   * @returns {string} 缓存键
   */
  getCacheKey(locale, key) {
    return `${locale}:${key}`
  }

  /**
   * 获取翻译内容（带缓存）
   * @param {string} locale - 语言代码
   * @param {string} key - 翻译键
   * @param {object} messages - 语言包消息
   * @returns {string|null} 翻译内容
   */
  getTranslation(locale, key, messages) {
    const cacheKey = this.getCacheKey(locale, key)
    const startTime = performance.now()
    
    // 检查缓存
    if (this.translationCache.has(cacheKey)) {
      const cached = this.translationCache.get(cacheKey)
      
      // 检查缓存是否过期
      if (Date.now() - cached.timestamp < this.config.ttl) {
        this.stats.hits++
        return cached.value
      } else {
        // 清除过期缓存
        this.translationCache.delete(cacheKey)
      }
    }
    
    // 缓存未命中，从语言包获取
    this.stats.misses++
    const translation = this.getNestedValue(messages, key)
    
    if (translation !== null) {
      // 存入缓存
      this.setCache(cacheKey, translation)
    }
    
    this.stats.loadTime += performance.now() - startTime
    return translation
  }

  /**
   * 从嵌套对象中获取值
   * @param {object} obj - 对象
   * @param {string} path - 路径（如 'common.loading'）
   * @returns {string|null} 值
   */
  getNestedValue(obj, path) {
    try {
      const keys = path.split('.')
      let current = obj
      
      for (const key of keys) {
        if (current && typeof current === 'object' && key in current) {
          current = current[key]
        } else {
          return null
        }
      }
      
      return typeof current === 'string' ? current : null
    } catch (error) {
      console.warn('获取嵌套值失败:', error)
      return null
    }
  }

  /**
   * 设置缓存
   * @param {string} key - 缓存键
   * @param {string} value - 缓存值
   */
  setCache(key, value) {
    // 检查缓存大小限制
    if (this.translationCache.size >= this.config.maxCacheSize) {
      this.evictOldestEntries()
    }
    
    // 检查内存使用量
    if (this.getEstimatedMemoryUsage() >= this.config.maxMemoryUsage) {
      this.evictLargestEntries()
    }
    
    this.translationCache.set(key, {
      value,
      timestamp: Date.now(),
      accessCount: 1,
      size: this.estimateStringSize(value)
    })
    
    this.updateStats()
  }

  /**
   * 清除最旧的缓存条目
   */
  evictOldestEntries() {
    const entries = Array.from(this.translationCache.entries())
    entries.sort((a, b) => a[1].timestamp - b[1].timestamp)
    
    // 清除最旧的25%条目
    const toRemove = Math.floor(entries.length * 0.25)
    for (let i = 0; i < toRemove; i++) {
      this.translationCache.delete(entries[i][0])
    }
  }

  /**
   * 清除最大的缓存条目
   */
  evictLargestEntries() {
    const entries = Array.from(this.translationCache.entries())
    entries.sort((a, b) => b[1].size - a[1].size)
    
    // 清除最大的10%条目
    const toRemove = Math.floor(entries.length * 0.1)
    for (let i = 0; i < toRemove; i++) {
      this.translationCache.delete(entries[i][0])
    }
  }

  /**
   * 估算字符串大小（字节）
   * @param {string} str - 字符串
   * @returns {number} 估算大小
   */
  estimateStringSize(str) {
    return new Blob([str]).size
  }

  /**
   * 获取估算的内存使用量
   * @returns {number} 内存使用量（字节）
   */
  getEstimatedMemoryUsage() {
    let totalSize = 0
    for (const [key, value] of this.translationCache) {
      totalSize += this.estimateStringSize(key) + value.size
    }
    return totalSize
  }

  /**
   * 更新统计信息
   */
  updateStats() {
    this.stats.cacheSize = this.translationCache.size
  }

  /**
   * 预加载常用翻译
   * @param {string} locale - 语言代码
   * @param {object} messages - 语言包消息
   * @returns {Promise} 预加载Promise
   */
  async preloadTranslations(locale, messages) {
    if (this.preloadStatus.isPreloading) {
      return this.preloadStatus.preloadPromise
    }
    
    this.preloadStatus.isPreloading = true
    this.preloadStatus.preloadPromise = this.performPreload(locale, messages)
    
    try {
      await this.preloadStatus.preloadPromise
    } finally {
      this.preloadStatus.isPreloading = false
    }
    
    return this.preloadStatus.preloadPromise
  }

  /**
   * 执行预加载
   * @param {string} locale - 语言代码
   * @param {object} messages - 语言包消息
   */
  async performPreload(locale, messages) {
    const startTime = performance.now()
    
    // 分批预加载，避免阻塞主线程
    const batchSize = 10
    const keys = this.config.preloadKeys
    
    for (let i = 0; i < keys.length; i += batchSize) {
      const batch = keys.slice(i, i + batchSize)
      
      // 预加载当前批次
      for (const key of batch) {
        const translation = this.getNestedValue(messages, key)
        if (translation) {
          const cacheKey = this.getCacheKey(locale, key)
          this.setCache(cacheKey, translation)
          this.preloadStatus.preloadedKeys.add(key)
        }
      }
      
      // 让出控制权，避免阻塞
      if (i + batchSize < keys.length) {
        await new Promise(resolve => setTimeout(resolve, 0))
      }
    }
    
    const loadTime = performance.now() - startTime
    console.log(`预加载完成: ${this.preloadStatus.preloadedKeys.size}个翻译, 耗时: ${loadTime.toFixed(2)}ms`)
  }

  /**
   * 清除缓存
   * @param {string} locale - 可选，指定语言的缓存
   */
  clearCache(locale = null) {
    if (locale) {
      // 清除指定语言的缓存
      const keysToDelete = []
      for (const key of this.translationCache.keys()) {
        if (key.startsWith(`${locale}:`)) {
          keysToDelete.push(key)
        }
      }
      keysToDelete.forEach(key => this.translationCache.delete(key))
    } else {
      // 清除所有缓存
      this.translationCache.clear()
      this.preloadStatus.preloadedKeys.clear()
    }
    
    this.updateStats()
  }

  /**
   * 获取缓存统计信息
   * @returns {object} 统计信息
   */
  getStats() {
    return {
      ...this.stats,
      hitRate: this.stats.hits / (this.stats.hits + this.stats.misses) || 0,
      memoryUsage: this.getEstimatedMemoryUsage(),
      preloadedCount: this.preloadStatus.preloadedKeys.size
    }
  }

  /**
   * 优化缓存
   * 清理过期和低频使用的缓存条目
   */
  optimizeCache() {
    const now = Date.now()
    const keysToDelete = []
    
    for (const [key, value] of this.translationCache) {
      // 清理过期缓存
      if (now - value.timestamp > this.config.ttl) {
        keysToDelete.push(key)
      }
      // 清理低频使用的缓存（访问次数少且时间久）
      else if (value.accessCount < 2 && now - value.timestamp > this.config.ttl / 2) {
        keysToDelete.push(key)
      }
    }
    
    keysToDelete.forEach(key => this.translationCache.delete(key))
    this.updateStats()
    
    return keysToDelete.length
  }

  /**
   * 增加访问计数
   * @param {string} locale - 语言代码
   * @param {string} key - 翻译键
   */
  incrementAccessCount(locale, key) {
    const cacheKey = this.getCacheKey(locale, key)
    if (this.translationCache.has(cacheKey)) {
      const cached = this.translationCache.get(cacheKey)
      cached.accessCount++
      cached.timestamp = Date.now() // 更新访问时间
    }
  }
}

// 创建全局缓存实例
const resourceCache = new ResourceCache()

// 定期优化缓存
if (typeof window !== 'undefined') {
  setInterval(() => {
    const cleaned = resourceCache.optimizeCache()
    if (cleaned > 0) {
      console.log(`缓存优化完成，清理了 ${cleaned} 个条目`)
    }
  }, 5 * 60 * 1000) // 每5分钟优化一次
}

export default resourceCache
export { ResourceCache }